// ═══════════════════════════════════════════════════════════════════════════
// InvoiceCreator
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { calcIepsBreakdown, getIepsRateForGL } from "../dominio/impuestos.js";
import { PRICING_RULES, resolverPrecio } from "../dominio/precios.js";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO, SAT_REGIMEN_FISCAL, SAT_USO_CFDI } from "../fiscal/catalogos-sat.js";
import { fmt } from "../pdf/comunes.js";
import { LOGO_BASE64 } from "../pdf/logo.js";
import { generatePDF } from "../pdf/remision.js";
import { InvoicePreview } from "./InvoicePreview.jsx";
import { PriceListPreview } from "./PriceListPreview.jsx";
import { leerBodegasDeSalida, bodegaRecordada, recordarBodega } from "../datos/bodegas.js";
import { Settings } from "./Settings.jsx";
import { ShippingPreview } from "./ShippingPreview.jsx";
import { Card, Label, NumInput, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { generateId } from "./utiles.js";
import { precargaDesdeRemision, puedeReemplazarse, ordenarCatalogo,
         describirCambios, preciosQueCambiaron } from "../dominio/remisiones.js";
import { pedidoUsual, describirPedido } from "../dominio/pedido-usual.js";
import { ajustarFormaPago, POR_DEFINIR, PPD } from "../dominio/forma-de-pago.js";
import { puedeMembresia } from "../auth/permisos.js";
import { useRemisionAcciones } from "./RemisionAcciones.jsx";

export function InvoiceCreator({ clients, products, folio, invoices, onSave, preload,
                                 membresia, setInvoices, onAbrirEnEditor }) {
  // La misma barra de acciones que en el resto: timbrar, PDF, pagos. Se usa
  // sobre la remisión recién guardada para que no haya que ir a buscarla.
  const acciones = useRemisionAcciones({
    clients, products, invoices, setInvoices: setInvoices || (() => {}),
    membresia, onAbrirEnEditor });
  const puedeFolioManual = puedeMembresia(membresia, "usar_folio_manual");
  const [selectedClient, setSelectedClient] = useState("");
  // Los renglones siguen SIEMPRE el orden alfabético del catálogo: de ellos
  // salen la captura, la vista previa y el PDF. Ver `ordenarCatalogo`.
  const catalogo = useMemo(() => ordenarCatalogo(products), [products]);
  const [lineItems, setLineItems] = useState(() => catalogo.map(p => ({ productId: p.id, qty: 0 })));

  // Sync lineItems when products change (new product added, product removed)
  useEffect(() => {
    setLineItems(prev => {
      const existing = new Map(prev.map(li => [li.productId, li]));
      return catalogo.map(p => existing.get(p.id) || { productId: p.id, qty: 0 });
    });
  }, [catalogo]);
  const [customDiscount, setCustomDiscount] = useState(null);
  const [customIva, setCustomIva] = useState(null);
  const [notes, setNotes] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [showShippingPreview, setShowShippingPreview] = useState(false);
  const [showPriceListPreview, setShowPriceListPreview] = useState(false);

  // ── De qué bodega sale ───────────────────────────────────────────────────
  //
  // Con UNA bodega no hay nada que preguntar y el selector no aparece: pedir
  // que se conteste una pregunta sin alternativas es ruido. Con varias —que
  // desde 0058 es lo normal, porque el vino pasa por maduración, producto
  // terminado y foráneas— la remisión tiene que decir de cuál salió, o el
  // inventario por bodega queda a merced de un desempate alfabético.
  const [bodegas, setBodegas] = useState([]);
  const [bodega, setBodega] = useState(null);

  // ── Editando una remisión existente (0064) ───────────────────────────────
  //
  // null = remisión nueva. Si no, { id, folio, date, clientId, precios }: el
  // documento que se corrige. Conserva folio y fecha, y los precios con que
  // se emitió. Va también en un ref porque el efecto de bodegas termina
  // cuando quiere y no debe pisar la bodega del documento.
  const [edicion, setEdicion] = useState(null);
  const edicionRef = useRef(null);
  useEffect(() => { edicionRef.current = edicion; }, [edicion]);

  useEffect(() => {
    let vivo = true;
    if (!membresia?.tenant_id) return;
    leerBodegasDeSalida(membresia.tenant_id)
      .then(bs => {
        if (!vivo) return;
        setBodegas(bs);
        // Al editar manda la bodega del documento, no la recordada.
        setBodega(b => edicionRef.current ? b : bodegaRecordada(membresia.tenant_id, bs));
      })
      // Que no se puedan leer NO impide emitir: sin bodega indicada el
      // servidor aplica su respaldo, que es lo que hacía hasta hoy.
      .catch(e => console.warn("[remisión] no se pudieron leer las bodegas:", e?.message));
    return () => { vivo = false; };
  }, [membresia?.tenant_id]);
  // La que se acaba de guardar, para no perderla de vista.
  const [recienCreada, setRecienCreada] = useState(null);

  // ── Precarga desde el historial (editar/duplicar) — lote 2.5 ──
  useEffect(() => {
    if (!preload) return;
    const { invoice, modo } = preload;
    const pre = precargaDesdeRemision(invoice, products, modo);
    if (!clients.some(c => c.id === pre.clientId)) {
      alert("❌ El cliente de esta remisión ya no existe en el catálogo.");
      return;
    }
    if (pre.faltantes.length) {
      alert("⚠️ Productos de la remisión que ya no están en el catálogo y NO se cargaron:\n\n• " +
        pre.faltantes.join("\n• ") + "\n\nRevisa las líneas antes de guardar.");
    }
    setSelectedClient(pre.clientId);
    const precargados = new Map(pre.lineItems.map(x => [x.productId, x]));
    setLineItems(catalogo.map(p => {
      const x = precargados.get(p.id);
      return { productId: p.id, qty: x?.qty || 0, ...(x?.cortesia ? { cortesia: x.cortesia } : {}) };
    }));
    setCustomDiscount(pre.discount);
    setCustomIva(pre.ivaRate);
    setNotes(pre.notes);
    setFormaPago(pre.formaPago);
    setMetodoPago(pre.metodoPago);
    setUsoCfdi(pre.usoCfdi);
    setRegimenFiscal(pre.regimenFiscal);
    // La bodega del documento; si no se sabe, null y el servidor conserva la suya.
    setBodega(pre.warehouse || null);
    // Si la lista de precios cambió desde la emisión, se pregunta. Corregir
    // una remisión después de cambiar precios suele ser justo para eso.
    let edicionNueva = pre.edicion;
    if (edicionNueva) {
      const suCliente = clients.find(c => c.id === pre.clientId);
      const cambios = preciosQueCambiaron(invoice, edicionNueva.precios, products,
                                          pid => resolverPrecio(pid, suCliente, products));
      const fmtP = (n) => "$" + n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const preciosDeHoy = cambios.length > 0 && confirm(
        `Los precios cambiaron desde que se emitió la remisión #${edicionNueva.folio}:\n\n` +
        cambios.map(c => `• ${c.nombre}: ${fmtP(c.antes)} → ${fmtP(c.hoy)}`).join("\n") +
        `\n\n¿Actualizarla a los precios de hoy?\n\n` +
        `Aceptar: precios de hoy. Cancelar: conserva los de la emisión.\n` +
        `Puedes cambiarlo antes de guardar.`);
      edicionNueva = { ...edicionNueva, cambiosDePrecio: cambios, preciosDeHoy };
    }
    edicionRef.current = edicionNueva;
    setEdicion(edicionNueva);
    setCustomFolio(null);
    window.scrollTo({ top: 0 });
  }, [preload]);

  const [customFolio, setCustomFolio] = useState(null); // null = use auto folio
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [formaPago, setFormaPago] = useState(null);     // null = from client
  const [metodoPago, setMetodoPago] = useState(null);
  const [usoCfdi, setUsoCfdi] = useState(null);
  // El régimen del RECEPTOR. Mismo patrón que los tres de al lado: null =
  // «el del cliente», y cualquier otro valor lo sustituye solo en esta
  // remisión. Una CSF puede traer varios (migración 0061).
  const [regimenFiscal, setRegimenFiscal] = useState(null);
  const dropdownRef = useRef(null);

  const client = clients.find(c => c.id === selectedClient);
  const effectiveFolio = customFolio !== null ? customFolio : folio;
  const discount = customDiscount !== null ? customDiscount : (client?.discount ?? 20);
  const ivaRate = customIva !== null ? customIva : (client?.ivaRate ?? 8);
  const metodoElegido = metodoPago !== null ? metodoPago : (client?.paymentTiming || "PUE");
  // PPD obliga a "por definir". No es una convención de la casa: una factura
  // que dice "se pagará después" no puede declarar de una vez cómo se pagó.
  // Se ajusta aquí para que la combinación imposible no se pueda ni construir.
  const effectiveMetodoPago = metodoElegido;
  const effectiveFormaPago = ajustarFormaPago(
    metodoElegido, formaPago !== null ? formaPago : (client?.paymentMethod || "03"));
  const effectiveUsoCfdi = usoCfdi !== null ? usoCfdi : (client?.cfdiUse || "G01");
  const regimenesDelCliente = Array.isArray(client?.regimenes) ? client.regimenes : [];
  const effectiveRegimen = regimenFiscal !== null ? regimenFiscal : (client?.taxSystem || "");

  // getPrice devuelve el precio BRUTO (sin impuestos) según la regla del cliente
  const getPrice = useCallback((productId) => {
    // Al editar, cada producto que ya estaba conserva el precio con que se
    // emitió, mientras no se cambie de cliente y el operador no haya pedido
    // los de hoy. Lo nuevo toma el de hoy.
    if (edicion && !edicion.preciosDeHoy && selectedClient === edicion.clientId &&
        edicion.precios?.[productId] !== undefined) return edicion.precios[productId];
    if (!client) return products.find(p => p.id === productId)?.grossPrice || 0;
    // Custom per-client prices take priority
    if (client.customPrices && client.customPrices[productId] !== undefined && client.customPrices[productId] !== "") {
      return Number(client.customPrices[productId]);
    }
    const rule = PRICING_RULES[client.pricingRule];
    if (rule?.priceMap && rule.priceMap[productId] !== undefined) return rule.priceMap[productId];
    return products.find(p => p.id === productId)?.grossPrice || 0;
  }, [client, products, edicion, selectedClient]);

  const activeLines = lineItems.filter(li => li.qty > 0);

  // ── El pedido usual del cliente ──────────────────────────────────────────
  // Sustituye al botón Duplicar, que el operador quitó por confuso. La
  // diferencia está en CUÁNDO aparece: aquí, al empezar la remisión, que es
  // donde sirve. Y solo si no se ha capturado nada — una vez que el usuario
  // empezó a teclear, ofrecerle rellenar sería estorbar.
  const sugerencia = useMemo(
    () => (activeLines.length === 0 ? pedidoUsual(invoices, selectedClient, products) : null),
    [invoices, selectedClient, products, activeLines.length]);

  const aplicarSugerencia = () => {
    if (!sugerencia) return;
    const porProducto = new Map(sugerencia.lineas.map(l => [l.productId, l.qty]));
    setLineItems(catalogo.map(p => ({ productId: p.id, qty: porProducto.get(p.id) || 0 })));
  };
  // Sobre lo COBRABLE. Las botellas de cortesía cuentan en `totalBottles`
  // —salen de la bodega y el transportista las carga— pero no en el dinero.
  const subtotalBeforeDiscount = activeLines.reduce(
    (sum, li) => sum + getPrice(li.productId) * (li.qty - (li.cortesia || 0)), 0);
  const discountAmount = subtotalBeforeDiscount * (discount / 100);
  const subtotal = subtotalBeforeDiscount - discountAmount;
  const iepsBreakdown = calcIepsBreakdown(activeLines, products, getPrice, discount);
  const iepsAmount = iepsBreakdown.totalIeps;
  const ivaAmount = (subtotal + iepsAmount) * (ivaRate / 100);
  const total = subtotal + iepsAmount + ivaAmount;
  const totalBottles = activeLines.reduce((sum, li) => sum + li.qty, 0);

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Guardar: nueva o corrección, con o sin PDF ───────────────────────────
  //
  // Antes eran dos funciones casi iguales, una por botón, y las dos decían
  // "las remisiones NO se editan… ¿continuar?". Ese aviso se aceptaba sin
  // leer, el servidor conservaba la original, y «Guardar + PDF» mandaba al
  // cliente lo capturado en pantalla. Ahora la corrección se guarda de verdad
  // y el PDF sale de lo que el servidor guardó.
  const construir = (existente) => ({
    id: existente?.id || generateId(),
    folio: existente ? existente.folio : effectiveFolio,
    // Una corrección es el MISMO documento: conserva su fecha de emisión.
    date: existente?.date || new Date().toISOString(),
    client: { ...client }, lines: activeLines.map(li => {
      const prod = products.find(p => p.id === li.productId);
      const price = getPrice(li.productId);
      return {
        ...li, productName: prod?.name,
        unitPrice: price, amount: price * li.qty,
        iepsRate: prod?.iepsRate ?? getIepsRateForGL(prod?.gl ?? 13),
      };
    }),
    discount, ivaRate, subtotalBeforeDiscount, discountAmount,
    subtotal, iepsAmount, iepsBreakdown: iepsBreakdown.byRate, ivaAmount, total, totalBottles, notes,
    formaPago: effectiveFormaPago, metodoPago: effectiveMetodoPago, usoCfdi: effectiveUsoCfdi,
    warehouse: bodega || undefined,
    regimenFiscal: effectiveRegimen || undefined,
    ...(existente ? { _editar: true } : {}),
  });

  const limpiar = () => {
    setLineItems(catalogo.map(p => ({ productId: p.id, qty: 0 })));
    setNotes("");
    setShowPreview(false);
    if (customFolio !== null) setCustomFolio(null);
    if (edicionRef.current) {
      edicionRef.current = null;
      setEdicion(null);
      setCustomDiscount(null); setCustomIva(null);
      setFormaPago(null); setMetodoPago(null); setUsoCfdi(null); setRegimenFiscal(null);
    }
  };

  const guardar = async ({ conPdf = false } = {}) => {
    if (!client || activeLines.length === 0) return;

    // Un folio tecleado que ya existe NO se sobrescribe por aquí: se corrige
    // con Editar, que carga sus precios, sus cortesías y su bodega.
    if (!edicion) {
      const ocupa = invoices.find(i => i.folio === effectiveFolio);
      if (ocupa) {
        alert(`La remisión #${effectiveFolio} ya existe.\n\nPara corregirla, ábrela con ` +
              `«Editar»: así se cargan sus precios, sus cortesías y su bodega, y queda ` +
              `escrito qué cambió.`);
        return;
      }
    }

    const existente = edicion ? invoices.find(i => i.id === edicion.id) : null;
    if (edicion && !existente) {
      alert("❌ La remisión que estabas editando ya no está en la lista. Recarga y vuelve a abrirla.");
      return;
    }
    if (existente) {
      const r = puedeReemplazarse(existente);
      if (!r.ok) { alert("❌ " + r.motivo); return; }
    }

    const inv = construir(existente);

    if (existente) {
      const cambios = describirCambios(existente, inv);
      if (!cambios.length) { alert("No hay cambios que guardar."); return; }
      if (!confirm(`Corregir la remisión #${existente.folio} · ${existente.client?.name || ""}\n\n` +
                   cambios.map(c => "• " + c).join("\n") +
                   `\n\nConserva su folio y su fecha. Queda registrado quién la ` +
                   `corrigió y qué cambió.`)) return;
      const guardada = await onSave(inv);
      if (!guardada) return;                 // no se guardó: nada cambia aquí
      if (conPdf) generatePDF(guardada);
      setRecienCreada(guardada);
      limpiar();
      alert(`✅ Remisión #${guardada.folio} corregida`);
      return;
    }

    onSave(inv);
    if (conPdf) generatePDF(inv);
    // ── No se pierde de vista lo que se acaba de crear ────────────────────
    // El operador: "después de facturar una remisión, me regresa a la
    // pantalla general y se me pierde la remisión que acabo de timbrar."
    setRecienCreada(inv);
    limpiar();
    if (bodega && membresia?.tenant_id) recordarBodega(membresia.tenant_id, bodega);
    // Solo se canta éxito de lo NUEVO; si el servidor falla, `saveInvoice`
    // lo encola y lo dice aparte.
    if (!conPdf) alert(`✅ Remisión #${effectiveFolio} guardada`);
  };

  const descartarEdicion = () => {
    if (!confirm("¿Descartar la corrección? La remisión se queda como estaba.")) return;
    setSelectedClient("");
    limpiar();
  };

  return (
    <div>
      <SectionTitle sub={edicion
          ? `Folio: ${edicion.folio} · emitida el ${new Date(edicion.date).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}`
          : `Folio: ${effectiveFolio} · ${new Date().toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`}>
        {edicion ? `Corregir Remisión #${edicion.folio}` : "Nueva Remisión"}
      </SectionTitle>

      {/* Que no quede duda de que se está corrigiendo un documento que ya
          existe, y de cómo salir sin tocarlo. */}
      {edicion && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: "#1f1a0d",
                      border: "1px solid #3a331a", borderRadius: 8, display: "flex",
                      alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12, color: "#e8d9a0" }}>
          <span style={{ flex: 1, minWidth: 220 }}>
            ✏️ Estás corrigiendo la remisión <b>#{edicion.folio}</b>. Conserva su folio, su
            fecha y {edicion.preciosDeHoy ? <b>los precios de hoy</b> : "los precios con que se emitió"}.
            Al guardar se recalcula en el servidor y queda registrado qué cambió.
            {edicion.cambiosDePrecio?.length > 0 && !edicion.preciosDeHoy && (
              <> Hoy {edicion.cambiosDePrecio.length === 1 ? "cambió el precio de" : "cambiaron los precios de"}{" "}
                {edicion.cambiosDePrecio.map(c => c.nombre).join(", ")}.</>
            )}
          </span>
          {edicion.cambiosDePrecio?.length > 0 && (
            <button onClick={() => setEdicion(e => {
                const otra = { ...e, preciosDeHoy: !e.preciosDeHoy };
                edicionRef.current = otra;
                return otra;
              })}
              style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11 }}>
              {edicion.preciosDeHoy ? "↩ Precios de la emisión" : "↻ Usar precios de hoy"}
            </button>
          )}
          <button onClick={descartarEdicion}
            style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11, borderColor: "#3a2020", color: "#c97070" }}>
            ✕ Descartar corrección
          </button>
        </div>
      )}

      {/* ── Lo que se acaba de guardar, con todo a la mano ─────────────────
          El operador: "después de facturar una remisión, me regresa a la
          pantalla general y se me pierde la remisión que acabo de timbrar."
          Se queda aquí, con su barra de acciones —la de siempre, que timbra
          por el servidor— hasta que se empiece otra. */}
      {recienCreada && (() => {
        // Se busca la versión VIVA: al guardar, el servidor asigna folio y la
        // app la actualiza. Mostrar la copia de hace un segundo enseñaría
        // "PENDIENTE" cuando el folio ya llegó.
        const viva = invoices.find(i => i.id === recienCreada.id) || recienCreada;
        return (
          <Card style={{ marginBottom: 16, borderColor: "#2a5a2a" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10,
                          flexWrap: "wrap", marginBottom: 10 }}>
              <span style={{ fontSize: 11, color: "#4ade80", letterSpacing: 2 }}>
                ✓ GUARDADA
              </span>
              <span style={{ color: "#c9a96e", fontWeight: 600 }}>
                {viva.folio ? `#${viva.folio}` : "folio pendiente"}
              </span>
              <span style={{ fontSize: 14, flex: 1, minWidth: 120 }}>{viva.client?.name}</span>
              <span style={{ color: "#c9a96e", fontWeight: 600 }}>{fmt(viva.total)}</span>
              <button onClick={() => setRecienCreada(null)}
                style={{ background: "transparent", border: "1px solid #222",
                         borderRadius: 5, color: "#777", fontSize: 11,
                         padding: "4px 10px", cursor: "pointer" }}>
                Empezar otra
              </button>
            </div>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 10, lineHeight: 1.7 }}>
              Ya está guardada. Desde aquí puedes timbrarla, verla o registrar un pago
              sin ir a buscarla.
            </div>
            {acciones.barraDe(viva)}
          </Card>
        );
      })()}

      {/* Custom folio override
          Solo para quien puede editar remisiones, porque es lo mismo: teclear
          un folio ocupado REEMPLAZA esa remisión. Era la puerta de atrás —se
          llegaba al editor sin pasar por el botón Editar— y el operador la
          cerró por su nombre: "no puede usar un folio distinto". */}
      {puedeFolioManual && !edicion && (
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {customFolio === null ? (
          <button onClick={() => setCustomFolio(folio > 1 ? folio - 1 : 1)}
            style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11, borderColor: "#2a2a3a", color: "#a0a0c9" }}>
            🔢 Usar folio diferente
          </button>
        ) : (
          <>
            <span style={{ fontSize: 11, color: "#888" }}>Folio manual:</span>
            <input type="number" min="1" value={customFolio}
              onChange={e => setCustomFolio(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ ...inputStyle, width: 80, textAlign: "center", padding: "4px 8px", fontSize: 14, fontWeight: 700, borderColor: "#3a3a1a", color: "#c9a96e" }} />
            {(() => {
              // Se avisa ANTES de capturar la remisión. Decir "se reemplazará"
              // sobre algo protegido es peor que no decir nada: el usuario
              // captura veinte líneas y se entera hasta darle a Guardar.
              const ocupa = invoices.find(i => i.folio === customFolio);
              if (!ocupa) return null;
              const r = puedeReemplazarse(ocupa);
              return r.ok ? (
                // Ya no se reemplaza tecleando el folio: se corrige con Editar.
                <button onClick={() => onAbrirEnEditor?.(ocupa, "editar")}
                  style={{ fontSize: 11, color: "#f0a050", padding: "3px 8px", background: "#1a1a0d",
                           borderRadius: 4, border: "1px solid #3a3a1a", cursor: "pointer" }}>
                  ⚠️ Folio #{customFolio} ya existe — ✏️ corregirla
                </button>
              ) : (
                <span title={r.motivo}
                  style={{ fontSize: 11, color: "#f87171", padding: "3px 8px", background: "#1f0d0d", borderRadius: 4, border: "1px solid #3a1a1a" }}>
                  🔒 Folio #{customFolio} está {ocupa.canceledAt ? "cancelado" : "timbrado"} — no se puede reemplazar
                </span>
              );
            })()}
            <button onClick={() => setCustomFolio(null)}
              style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11, borderColor: "#3a2020", color: "#c97070" }}>
              ✕ Usar folio automático ({folio})
            </button>
          </>
        )}
      </div>
      )}

      {/* Client Selection */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1e1e1e", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>👤</div>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e" }}>Seleccionar Cliente</span>
        </div>

        <div ref={dropdownRef} style={{ position: "relative" }}>
          <input
            type="text"
            placeholder="Buscar cliente por nombre o ciudad..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            style={{ ...inputStyle, paddingLeft: 36 }}
          />
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#555" }}>🔍</span>

          {showDropdown && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, maxHeight: 280, overflowY: "auto",
              background: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "0 0 8px 8px", zIndex: 100,
            }}>
              {filteredClients.map(c => (
                <div key={c.id} onClick={() => {
                  setSelectedClient(c.id); setSearchTerm(c.name); setShowDropdown(false);
                  setCustomDiscount(null); setCustomIva(null);
                  setFormaPago(null); setMetodoPago(null); setUsoCfdi(null);
                }} style={{
                  padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #222",
                  background: selectedClient === c.id ? "#252015" : "transparent",
                  transition: "background 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.background = "#1e1e1e"}
                  onMouseLeave={e => e.currentTarget.style.background = selectedClient === c.id ? "#252015" : "transparent"}
                >
                  <div style={{ fontSize: 14, color: "#e8e0d4" }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{c.city}, {c.state} {c.rfc ? `· RFC: ${c.rfc}` : ""}</div>
                </div>
              ))}
              {filteredClients.length === 0 && <div style={{ padding: 14, color: "#555", fontSize: 13 }}>No se encontraron clientes</div>}
            </div>
          )}
        </div>

        {/* ── El pedido usual de este cliente ────────────────────────────── */}
        {client && sugerencia && (
          <div style={{ marginTop: 12, padding: "12px 16px", background: "#12140d",
                        border: "1px solid #2a3a1a", borderRadius: 8,
                        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 13, color: "#c9d8a0" }}>
                ¿Agregar el pedido usual de {client.name}?
              </div>
              {/* Se muestra QUÉ se va a agregar y en qué se basa. Una sugerencia
                  que no se puede revisar antes de aceptarla es una trampa. */}
              <div style={{ fontSize: 11, color: "#7a8a5a", marginTop: 3 }}>
                {describirPedido(sugerencia, products)}
              </div>
              <div style={{ fontSize: 10, color: "#556" , marginTop: 2 }}>
                {sugerencia.etiqueta}
              </div>
            </div>
            <button onClick={aplicarSugerencia}
              style={{ ...btnSecondary, padding: "8px 16px", fontSize: 12,
                       borderColor: "#3a5a1a", color: "#a8d060" }}>
              ＋ Agregar
            </button>
          </div>
        )}

        {client && (
          <div style={{ marginTop: 16, padding: 16, background: "#0d0d0d", borderRadius: 8 }}>
            {/* Fiscal status banner */}
            {client.rfc && client.taxSystem && client.domicilioFiscalZip && client.legalName ? (
              <div style={{ marginBottom: 12, padding: "6px 12px", background: "#0d1f0d", borderRadius: 6, border: "1px solid #1a3a1a", fontSize: 12, color: "#4ade80", display: "flex", alignItems: "center", gap: 6 }}>
                ✓ Datos fiscales completos — listo para timbrar CFDI
              </div>
            ) : client.rfc ? (
              <div style={{ marginBottom: 12, padding: "6px 12px", background: "#1f1a0d", borderRadius: 6, border: "1px solid #3a3a1a", fontSize: 12, color: "#facc15", display: "flex", alignItems: "center", gap: 6 }}>
                ⚠ Tiene RFC pero faltan datos fiscales para CFDI — edita el cliente y sube su CSF
              </div>
            ) : null}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
              {client.legalName && <div style={{ gridColumn: "1/-1" }}><span style={{ color: "#666", fontSize: 11 }}>RAZÓN SOCIAL</span><div style={{ fontSize: 13, marginTop: 3, color: "#c9a96e" }}>{client.legalName}</div></div>}
              <div><span style={{ color: "#666", fontSize: 11 }}>DIRECCIÓN</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.street}, {client.colonia}</div></div>
              <div><span style={{ color: "#666", fontSize: 11 }}>CIUDAD</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.city}, {client.state} CP {client.cp}</div></div>
              <div><span style={{ color: "#666", fontSize: 11 }}>TELÉFONO</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.phone || "—"}</div></div>
              <div><span style={{ color: "#666", fontSize: 11 }}>RFC</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.rfc || "Sin RFC"}</div></div>
              {client.taxSystem && <div><span style={{ color: "#666", fontSize: 11 }}>RÉGIMEN FISCAL</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.taxSystem} – {SAT_REGIMEN_FISCAL.find(r => r.code === client.taxSystem)?.label || ""}</div></div>}
              {client.domicilioFiscalZip && <div><span style={{ color: "#666", fontSize: 11 }}>CP FISCAL</span><div style={{ fontSize: 13, marginTop: 3, fontFamily: "monospace" }}>{client.domicilioFiscalZip}</div></div>}
              {client.att && <div><span style={{ color: "#666", fontSize: 11 }}>ATENCIÓN</span><div style={{ fontSize: 13, marginTop: 3 }}>{client.att}</div></div>}
              <div><span style={{ color: "#666", fontSize: 11 }}>REGLA DE PRECIO</span><div style={{ fontSize: 13, marginTop: 3, color: "#c9a96e" }}>{PRICING_RULES[client.pricingRule]?.label}</div></div>
            </div>
          </div>
        )}
      </Card>

      {/* Tax & Payment Settings */}
      {client && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e" }}>Impuestos y Pago</span>
            <span style={{ fontSize: 11, color: "#555" }}>(del perfil del cliente, ajustable por remisión)</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <div>
              <Label>Descuento %</Label>
              <NumInput value={discount} onChange={v => setCustomDiscount(v)} style={inputStyle} min={0} max={100} />
            </div>
            <div>
              <Label>IVA %</Label>
              <select value={ivaRate} onChange={e => setCustomIva(parseFloat(e.target.value))} style={selectStyle}>
                <option value={8}>8% (Frontera)</option>
                <option value={16}>16% (Interior)</option>
              </select>
            </div>
            <div>
              <Label>IEPS</Label>
              <div style={{ padding: "10px 12px", background: "#0d0d0d", borderRadius: 6, border: "1px solid #2a2a2a", fontSize: 12, color: "#888" }}>
                Automático por producto
                <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>≤14° → 26.5% · ≤20° → 30% · &gt;20° → 53%</div>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
            <div>
              <Label>Forma de Pago</Label>
              <select value={effectiveFormaPago} onChange={e => setFormaPago(e.target.value)}
                disabled={effectiveMetodoPago === PPD} style={{ ...selectStyle,
                  opacity: effectiveMetodoPago === PPD ? 0.6 : 1 }}>
                {SAT_FORMA_PAGO.map(f => <option key={f.code} value={f.code}>{f.code} – {f.label}</option>)}
              </select>
              {effectiveMetodoPago === PPD && (
                <div style={{ fontSize: 10, color: "#c9a96e", marginTop: 4, lineHeight: 1.6 }}>
                  En parcialidades es forzosamente <b>{POR_DEFINIR} — por definir</b>:
                  todavía no se sabe cómo se va a pagar. Se dirá en el complemento de
                  cada pago.
                </div>
              )}
            </div>
            <div>
              <Label>Método de Pago</Label>
              <select value={effectiveMetodoPago} onChange={e => setMetodoPago(e.target.value)} style={selectStyle}>
                {SAT_METODO_PAGO.map(m => <option key={m.code} value={m.code}>{m.code} – {m.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Uso de CFDI</Label>
              <select value={effectiveUsoCfdi} onChange={e => setUsoCfdi(e.target.value)} style={selectStyle}>
                {SAT_USO_CFDI.map(u => <option key={u.code} value={u.code}>{u.code} – {u.label}</option>)}
              </select>
            </div>
            {/* ── Con qué régimen del cliente ───────────────────────────────
                Solo con más de uno. Con uno solo no hay nada que escoger, y
                el CFDI usa el del cliente como siempre. La lista sale de su
                CSF, así que solo ofrece regímenes que el SAT le reconoce. */}
            {regimenesDelCliente.length > 1 && (
              <div>
                <Label>Régimen del cliente</Label>
                <select value={effectiveRegimen}
                  onChange={e => setRegimenFiscal(e.target.value)} style={selectStyle}>
                  {regimenesDelCliente.map(r => (
                    <option key={r.code} value={r.code}>{r.code} – {r.label}</option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                  Su CSF registra {regimenesDelCliente.length}. Se queda guardado en la remisión.
                </div>
              </div>
            )}

            {/* ── De qué bodega sale ────────────────────────────────────────
                Solo con más de una. Con una sola no hay nada que elegir y el
                selector sería una pregunta sin alternativas; el servidor
                aplica su respaldo igual que siempre. */}
            {bodegas.length > 1 && (
              <div>
                <Label>Sale de</Label>
                <select value={bodega || ""} onChange={e => setBodega(e.target.value)} style={selectStyle}>
                  {bodegas.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}{b.sede ? ` · ${b.sede}` : ""}{b.is_outbound ? "" : " (no es de salida)"}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                  De aquí se descuentan las botellas. Se recuerda para la próxima.
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Line Items */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, color: "#c9a96e" }}>Productos</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #2a2a2a" }}>
                <th style={{ textAlign: "left", padding: "8px 6px", fontSize: 11, color: "#666", letterSpacing: 1 }}>PRODUCTO</th>
                <th style={{ textAlign: "center", padding: "8px 6px", fontSize: 11, color: "#666", width: 80 }}>P. BRUTO</th>
                <th style={{ textAlign: "center", padding: "8px 6px", fontSize: 11, color: "#666", width: 55 }}>IEPS</th>
                <th style={{ textAlign: "center", padding: "8px 6px", fontSize: 11, color: "#666", width: 90 }}>CANTIDAD</th>
                <th style={{ textAlign: "right", padding: "8px 6px", fontSize: 11, color: "#666", width: 100 }}>IMPORTE</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => {
                const prod = products.find(p => p.id === li.productId);
                const price = getPrice(li.productId);
                return (
                  <tr key={li.productId} style={{ borderBottom: "1px solid #1a1a1a", background: li.qty > 0 ? "#15130d" : "transparent" }}>
                    <td style={{ padding: "10px 6px" }}>
                      <div style={{ fontSize: 13, color: li.qty > 0 ? "#e8e0d4" : "#555" }}>{prod?.name}</div>
                      {/* Se fueron los cuatro `category === "cortesia"` que
                          había aquí: el producto falso ya no existe, y la
                          cortesía es de las botellas, no del renglón entero. */}
                      <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>
                        {`${prod?.gl || "?"}° G.L. · Neto @${ivaRate}%: ${fmt(price * (1 + (prod?.iepsRate || 26.5)/100) * (1 + ivaRate/100))}`}
                        <span style={{ marginLeft: 8, color: (prod?.stock || 0) > 0 ? "#4ade80" : "#666" }}>
                          📦 {prod?.stock || 0}
                        </span>
                        {li.qty > 0 && li.qty > (prod?.stock || 0) && (
                          <span style={{ marginLeft: 6, color: "#f87171", fontSize: 9 }}>⚠️ excede stock</span>
                        )}
                      </div>
                      {/* Cuántas de estas van de regalo. Aparece solo con
                          botellas en el renglón: ofrecer regalar de algo que
                          no se está vendiendo no significa nada. */}
                      {li.qty > 0 && (
                        <div style={{ fontSize: 10, color: "#666", marginTop: 6, display: "flex",
                                      alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span>🎁 de cortesía:</span>
                          <NumInput value={li.cortesia || 0} integer min={0}
                            onChange={v => {
                              const updated = [...lineItems];
                              // Nunca más de las que se entregan: la base tiene
                              // la misma restricción y rechazaría la remisión
                              // entera al guardar.
                              updated[i] = { ...updated[i], cortesia: Math.min(Math.max(0, v), updated[i].qty) };
                              setLineItems(updated);
                            }}
                            style={{ ...inputStyle, width: 46, textAlign: "center", padding: "2px 2px", fontSize: 11 }} />
                          {(li.cortesia || 0) > 0 && (
                            <span style={{ color: "#70c9c9" }}>
                              se cobran {li.qty - li.cortesia} · salen {li.qty}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: "center", fontSize: 13, color: "#c9a96e" }}>{fmt(price)}</td>
                    <td style={{ textAlign: "center", fontSize: 11, color: "#888" }}>{prod?.iepsRate || getIepsRateForGL(prod?.gl || 13)}%</td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                        <button onClick={() => {
                          const updated = [...lineItems];
                          updated[i] = { ...updated[i], qty: Math.max(0, updated[i].qty - 1) };
                          setLineItems(updated);
                        }} style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #333", background: "#1a1a1a", color: "#999", cursor: "pointer", fontSize: 16 }}>−</button>
                        <NumInput value={li.qty} integer
                          onChange={v => {
                            const updated = [...lineItems];
                            updated[i] = { ...updated[i], qty: Math.max(0, v) };
                            setLineItems(updated);
                          }}
                          style={{ ...inputStyle, width: 50, textAlign: "center", padding: "4px 2px" }}
                        />
                        <button onClick={() => {
                          const updated = [...lineItems];
                          updated[i] = { ...updated[i], qty: updated[i].qty + 1 };
                          setLineItems(updated);
                        }} style={{ width: 28, height: 28, borderRadius: 6, border: "1px solid #333", background: "#1a1a1a", color: "#999", cursor: "pointer", fontSize: 16 }}>+</button>
                      </div>
                    </td>
                    <td style={{ textAlign: "right", fontSize: 13, color: li.qty > 0 ? (price === 0 ? "#70c9c9" : "#e8e0d4") : "#444", paddingRight: 6 }}>
                      {li.qty > 0
                        ? (price === 0 || li.qty - (li.cortesia || 0) <= 0
                            ? "cortesía"
                            : fmt(price * (li.qty - (li.cortesia || 0))))
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        {totalBottles > 0 && (
          <div style={{ marginTop: 20, borderTop: "1px solid #2a2a2a", paddingTop: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 340, marginLeft: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#888" }}>Importe bruto ({totalBottles} bot.)</span>
                <span>{fmt(subtotalBeforeDiscount)}</span>
              </div>
              {discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#c97070" }}>Descuento ({discount}%)</span>
                <span style={{ color: "#c97070" }}>-{fmt(discountAmount)}</span>
              </div>}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#888" }}>Base gravable</span>
                <span>{fmt(subtotal)}</span>
              </div>
              {iepsBreakdown.byRate.map(b => (
                <div key={b.rate} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: "#888" }}>IEPS ({b.rate}%)</span>
                  <span>{fmt(b.ieps)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                <span style={{ color: "#888" }}>IVA ({ivaRate}%)</span>
                <span>{fmt(ivaAmount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 600, borderTop: "1px solid #333", paddingTop: 10, marginTop: 4 }}>
                <span style={{ color: "#c9a96e" }}>TOTAL NETO</span>
                <span style={{ color: "#c9a96e" }}>{fmt(total)}</span>
              </div>
              <div style={{ textAlign: "right", fontSize: 11, color: "#888", marginTop: 4 }}>
                Total botellas: {totalBottles}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Notes */}
      <Card style={{ marginBottom: 20 }}>
        <Label>Notas / Observaciones</Label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={3} placeholder="Notas adicionales para esta remisión..."
          style={{ ...inputStyle, resize: "vertical" }} />
      </Card>

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
        <button onClick={() => setShowPreview(true)} disabled={!client || activeLines.length === 0}
          style={{ ...btnSecondary, opacity: (!client || activeLines.length === 0) ? 0.4 : 1 }}>
          👁 Vista Previa
        </button>
        <button onClick={() => guardar()} disabled={!client || activeLines.length === 0}
          style={{ ...btnSecondary, opacity: (!client || activeLines.length === 0) ? 0.4 : 1, borderColor: "#3a3a2a", color: "#c9a96e" }}>
          💾 {edicion ? `Guardar corrección #${edicion.folio}` : `Guardar Remisión #${effectiveFolio}`}
        </button>
        <button onClick={() => guardar({ conPdf: true })} disabled={!client || activeLines.length === 0}
          style={{ ...btnPrimary, opacity: (!client || activeLines.length === 0) ? 0.4 : 1 }}>
          📄 {edicion ? "Guardar corrección + PDF" : "Guardar + Descargar PDF"}
        </button>
        <button onClick={() => setShowShippingPreview(true)} disabled={!client || activeLines.length === 0}
          style={{ ...btnSecondary, opacity: (!client || activeLines.length === 0) ? 0.4 : 1, borderColor: "#1a3a3a", color: "#70c9c9" }}>
          📦 Guía de Envío
        </button>
        <button onClick={() => { if (client) setShowPriceListPreview(true); }}
          disabled={!client}
          style={{ ...btnSecondary, opacity: !client ? 0.4 : 1, borderColor: "#2a2a3a", color: "#a0a0c9" }}>
          📋 Lista de Precios
        </button>
        {/* El botón de "Guardar y Timbrar" se retiró de aquí.

            Timbraba con la llave de Facturapi guardada EN EL NAVEGADOR, y esa
            llave se purgó en 0.16.3 cuando el timbrado pasó al servidor: o sea
            que llevaba versiones lanzando "No hay API key configurada" al
            primer clic. Un botón muerto en el sitio más visible de la app.

            Y aunque funcionara, sobraba: era una SEGUNDA forma de timbrar, con
            reglas propias, sobre la misma remisión — exactamente el defecto que
            este proyecto ya corrigió una vez ("la misma remisión tenía poderes
            distintos según la pantalla por la que llegaras").

            Ahora se guarda, y se timbra desde la barra de acciones que aparece
            arriba con la remisión recién creada. */}
      </div>

      {/* Preview Modal */}
      {showPreview && client && (
        <InvoicePreview
          folio={edicion ? edicion.folio : effectiveFolio} client={client} lines={activeLines} products={products} getPrice={getPrice}
          discount={discount} ivaRate={ivaRate}
          subtotalBeforeDiscount={subtotalBeforeDiscount} discountAmount={discountAmount}
          subtotal={subtotal} iepsAmount={iepsAmount} iepsBreakdown={iepsBreakdown.byRate}
          ivaAmount={ivaAmount} total={total}
          totalBottles={totalBottles} notes={notes}
          formaPago={effectiveFormaPago} metodoPago={effectiveMetodoPago} usoCfdi={effectiveUsoCfdi}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Shipping Preview Modal */}
      {showShippingPreview && client && (
        <ShippingPreview
          folio={edicion ? edicion.folio : effectiveFolio} client={client} lines={activeLines} products={products} getPrice={getPrice}
          totalBottles={totalBottles} notes={notes}
          onClose={() => setShowShippingPreview(false)}
        />
      )}

      {/* Price List Preview Modal */}
      {showPriceListPreview && client && (
        <PriceListPreview
          client={client} products={products} getPrice={getPrice} discount={discount}
          vinicola={{ nombre: membresia?.tenant?.name }}
          onClose={() => setShowPriceListPreview(false)}
        />
      )}

      {/* Timbrar CFDI Preview + Confirmation */}

    </div>
  );
}
